import React, {useMemo} from "react";
import {RemoteValue} from "../../../util/RemoteValue";
import {useEmployeeStore} from "../../../api/EmployeeStore";
import {useEmployeeKeyHistoryStore} from "../../../api/EmployeeKeyHistoryStore";
import {useResidenceStore} from "../../../api/ResidenceStore";
import {DashboardWidget, DashboardWidgetItem} from "../DashboardWidget";
import {AppPages, usePageUrl} from "../../../app-layout/AppPages";
import {Typography} from "@mui/material";
import EmployeeAvatar from "../../../components/employee-avatar/EmployeeAvatar";
import EmployeeLink from "../../../components/employee-link/EmployeeLink";
import {EmployeeHistoryButton} from "../../history-dialog/HistoryDialogContext";
import {useToday} from "../../../util/DateUtil";
import {stringCmp} from "../../../util/SortUtil";
import {Employees_Read, KeyData_Read, Offices_Read, WorkSites_Read} from "../../../util/access/Permission";
import {CsvModel} from "../../../util/CSVWriteUtil";
import {Employee} from "../../../api/Model";

const residentsWithoutKeysCsvModel = RemoteValue.fromValue(new CsvModel<Employee>([
    {header: 'Person', value: employee => employee.fullName},
    {header: 'Email', value: employee => employee.email},
    {header: 'Start Date', value: employee => employee.startDate ?? ''},
]));

export function ResidentsWithoutKeysWidget() {
    const pageUrl = usePageUrl();
    const employees = useEmployeeStore(state => state.remoteValue);
    const keyHistory = useEmployeeKeyHistoryStore(state => state.remoteValue);
    const residency = useResidenceStore().remoteValue;
    const problems = useMemo(() => RemoteValue.combine({employees, keyHistory, residency})
        .map(values =>
            values.employees
                .filter(employee =>
                    !employee.deactivated && values.residency.isResident(employee) && values.keyHistory.active(employee).length === 0
                )
                .sort(stringCmp(employee => employee.startDate ?? '1900-01-01'))
        ), [employees, keyHistory, residency]);
    const today = useToday();


    return (
        <DashboardWidget
            title="Residents without keys"
            description="Employees that belong to this office but not having an key assigned to them. Oftentimes, it may signal that they actually have a key but this key is not assigned to them in this app. They should be the first candidates when you investigate the ghost key usages."
            problems={problems}
            requiredPermissions={[Employees_Read, KeyData_Read, Offices_Read, WorkSites_Read]}
            link={pageUrl(AppPages.employees, {}, {residency: -1000, hasKey: 'no-key', active: 'active'})}
            csvModel={residentsWithoutKeysCsvModel}
            renderProblem={(employee, index) => (
                <DashboardWidgetItem
                    key={employee.id}
                    index={index}
                    left={
                        <>
                            <EmployeeAvatar employee={employee} sx={{width: 24, height: 24}}/>
                            <EmployeeLink employee={employee}/>
                        </>
                    }
                    right={
                        <>
                            {employee.startDate && (
                                <>
                                    <Typography variant="body2" color="text.secondary">Since</Typography>
                                    <Typography
                                        variant="body2"
                                        sx={employee.startDate && employee.startDate <= today ? {
                                            color: 'text.primary',
                                            fontWeight: 'bold'
                                        } : {
                                            color: 'text.secondary'
                                        }}>{employee.startDate}</Typography>
                                </>
                            )}
                            <EmployeeHistoryButton employee={employee}/>
                        </>
                    }
                />
            )}
        />
    );
}
